import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import {
  isFormattedMontoSubfield,
  resolveSubfieldMeta,
  sanitizeType48ScalarString,
  sanitizeType48SubfieldValue,
} from 'src/app/trackvision/shared/utils/customfield-value-sanitize.util';

@Injectable({
  providedIn: 'root'
})
export class CustomfieldsService {
  constructor(private http: HttpClient) { }

  getCustomfieldsByRequirementType(requirementTypeId) {
    return this.http.get(`${environment.apiUrl}/trackvision/customfield/requirement-type/${requirementTypeId}`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '')
    })
  }

  getCreationCustomfieldsByRequirementType(requirementTypeId) {
    return this.http.get(`${environment.apiUrl}/trackvision/customfield/list/${requirementTypeId}`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '')
    })
  }

  /** Opciones cascada para CF con model.dependsOn (common.counterparty). */
  postModelOptions(body: { customfieldId: number; parentValue: number | string }) {
    return this.http.post(`${environment.apiUrl}/trackvision/customfield/model-options`, body, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '')
    });
  }

  getMassiveUploadTemplateFields(requirementTypeId: number, flowType?: string) {
    const params = flowType ? { flowType } : {};
    return this.http.get(`${environment.apiUrl}/trackvision/customfield/massive-upload-template/${requirementTypeId}`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || ''),
      params
    })
  }
  filterArray(array, element, value, opertor) {
    return opertor === '!==' ? array.filter((item) => item[element] !== value) : array.filter((item) => item[element] === value);
  }
  getCustomfield(array, key, value) {
    return array.find(el => typeof el[key] === 'number' ? el[key] === Number(value) : el[key] === String(value));
  }
  getPositionCustomfield(array, value) {
    return array.indexOf(value)
  }
  initialSubgroupLoad(customfieldsRequirementType, resultCustomfieldsSubGrop) {

    let clonCustomfieldsArray = Object.assign([], customfieldsRequirementType);
    let i: number = 0;
    const ids = clonCustomfieldsArray.map(el => el.id);
    let subGroup = [];
    while (i < resultCustomfieldsSubGrop.length) {
      let indexSubGroup = this.getPositionCustomfield(ids, resultCustomfieldsSubGrop[i].id);
      // Verificar que indexSubGroup sea válido antes de usar
      if (indexSubGroup >= 0 && clonCustomfieldsArray[indexSubGroup]) {
        subGroup.push(clonCustomfieldsArray[indexSubGroup]);
        // Verificar que el elemento se agregó correctamente antes de asignar propiedades
        if (subGroup[i]) {
          subGroup[i]['customfields'] = [];
          resultCustomfieldsSubGrop[i].values.map(element => {
            if (!element?.name) return;
            const child = this.getCustomfield(clonCustomfieldsArray, 'name', element.name);
            if (!child) {
              console.warn('[initialSubgroupLoad] Campo no encontrado en grupo', resultCustomfieldsSubGrop[i]?.name, element.name);
              return;
            }
            subGroup[i]['customfields'].push(child);
            customfieldsRequirementType = this.filterArray(customfieldsRequirementType, 'name', element.name, '!==');
          });
        }
      }
      i++;
    }
    return { customfields: customfieldsRequirementType, result: resultCustomfieldsSubGrop };
  }

  extractData(array, id, fieldTypesMap?: any) {
    let subGroup = [];
    let data = this.getCustomfield(array, 'id', id);
    if (data) {
      // Solo quitar $ y % del string JSON, NO quitar puntos (se procesarán después en el JSON parseado)
      let cleanedValue = data.value.replace(/\$/g, "").replace(/%/g, '').trim();

      try {
        const parsedArray = JSON.parse(cleanedValue || '[]');

        // Si se proporciona fieldTypesMap, procesar cada campo preservando puntos de emails
        if (fieldTypesMap && Array.isArray(parsedArray)) {
          parsedArray.forEach((element: any) => {
            if (element && typeof element === 'object') {
              const processedElement = { ...element };
              Object.keys(element).forEach(fieldId => {
                const fieldValue = element[fieldId];
                const subMeta = resolveSubfieldMeta(fieldTypesMap, fieldId);
                if (fieldValue != null && fieldValue !== '') {
                  processedElement[fieldId] = sanitizeType48SubfieldValue(fieldValue, subMeta);
                }
              });

              // Agrupar por campo
              Object.keys(processedElement).forEach(e => {
                if (!subGroup[e]) {
                  subGroup[e] = [];
                }
                subGroup[e].push(processedElement[e]);
              });
            } else {
              // Si no es objeto, usar el elemento original
              Object.keys(element).forEach(e => {
                if (!subGroup[e]) {
                  subGroup[e] = [];
                }
                subGroup[e].push(element[e]);
              });
            }
          });
        } else {
          // Si no hay fieldTypesMap, comportamiento original (quitar puntos de todo)
          cleanedValue = cleanedValue.replace(/\./g, "");
          JSON.parse(cleanedValue || '[]').forEach(element => Object.keys(element).forEach(e => {
            if (!subGroup[e]) {
              subGroup[e] = [];
            }
            subGroup[e].push(element[e]);
          }));
        }
      } catch (e) {
        // Si falla el parseo, usar comportamiento original
        cleanedValue = cleanedValue.replace(/\./g, "");
        try {
          JSON.parse(cleanedValue || '[]').forEach(element => Object.keys(element).forEach(e => {
            if (!subGroup[e]) {
              subGroup[e] = [];
            }
            subGroup[e].push(element[e]);
          }));
        } catch (parseError) {
          console.error('Error parsing JSON in extractData:', parseError);
        }
      }
    }
    return subGroup;
  }

  /**
   * Normaliza montos formateados (tipo 2 + format + regularexpression) dentro del JSON
   * guardado de un campo agrupador tipo 48 (mismo criterio que requirement-detail al guardar).
   * Útil para modal de cambio de estado (action/apply) y otros flujos que armaron el payload
   * con símbolos $ / puntos / comas en el subcampo.
   */
  normalizeType48FieldValueJson(valueStr: string | null | undefined, type48Field: { id?: any; customfields?: any[] }): string {
    if (valueStr == null || String(valueStr).trim() === '') {
      return valueStr as string;
    }
    let parsed: any;
    try {
      parsed = typeof valueStr === 'string' ? JSON.parse(valueStr) : valueStr;
    } catch {
      return valueStr as string;
    }
    if (!Array.isArray(parsed) || !type48Field?.customfields?.length) {
      return typeof valueStr === 'string' ? valueStr : JSON.stringify(valueStr);
    }
    const normalized = parsed.map((row: any) => {
      if (!row || typeof row !== 'object') {
        return row;
      }
      const out: any = { ...row };
      for (const key of Object.keys(out)) {
        const subField = type48Field.customfields.find(
          (cf: any) => cf.id == key || String(cf.id) === String(key) || cf.id === Number(key)
        );
        if (!isFormattedMontoSubfield(subField)) {
          continue;
        }
        const v = out[key];
        if (v == null) {
          continue;
        }
        const raw = Array.isArray(v) ? v[0] : v;
        out[key] = sanitizeType48ScalarString(raw, subField);
      }
      return out;
    });
    return JSON.stringify(normalized);
  }

  parseCustomfielsMultiple(array) {
    return array.map(el => el.hasOwnProperty('values') ? el.values : el);
  }
  isType(value, type) {
    return typeof value === type;
  }

  /**
   * Retorna lista de customfieldsgroups dado el el tipo de requerimiento
   * @param requirenmenttypeid
   * @returns
   */
  async getCustomGropuByRequirenment(requirenmenttypeid: any) {
    const headers = new HttpHeaders().set('Token', localStorage.getItem('access_token') || '');
    const resp:any = await firstValueFrom(this.http.get(`${environment.apiUrl}/common/customfield/getcustomfieldsgroups/${requirenmenttypeid}`, { headers }));
    return resp.result.map((item:any) => { return {id: item.id, text: item.name}})
  }


  async saveCustomfield(body:any):Promise<any>{
    const headers = new HttpHeaders().set('Token', localStorage.getItem('access_token') || '');
    const path = body.id ? `${environment.apiUrl}/common/customfield/update` : `${environment.apiUrl}/common/customfield/create`
    const resp:any = await firstValueFrom(this.http.post(path, body, { headers }));
    return resp;
  }

  /**
   * hidden/show customfield
   * @param body
   */
  async customfieldHidden(body):Promise<any>{
    const headers = new HttpHeaders().set('Token', localStorage.getItem('access_token') || '');
    const resp:any = await firstValueFrom(this.http.post(`${environment.apiUrl}/common/customfield/hidden`, body, { headers }));
    return resp;
  }

}
